import { Box, styled } from '@mui/material';
import React, { FC, PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Tbooleanish, Booleanish } from 'types/booleanish.types';
import { TBaseTableTypes, TTableColumn, TTableRowProps } from './table.types';
import TableCell, { TRequiredColumnProps } from './TableCell';
import { stopPropagation } from 'utils/event.utils';

const StyledTableRow = styled('div')<{
  clickable?: Tbooleanish;
  selected?: Tbooleanish;
  'has-collapsable'?: Tbooleanish;
}>(({ theme, clickable, selected, 'has-collapsable': hasCollapsable }) => ({
  position: 'relative',
  zIndex: 2,
  display: 'flex',
  width: 'calc(100% - 16px)',
  borderRadius: '6px',
  background: theme.custom.themeColors.white,
  boxShadow: theme.custom.shadows[4],
  alignItems: 'center',
  padding: theme.spacing(2),
  maxHeight: '68px',

  ...(clickable === 'true'
    ? {
        cursor: 'pointer',
        '&, & *': {
          cursor: 'pointer !important', // inherit pointer cursor to all children
        },
      }
    : {
        cursor: 'default',
      }),

  // Showing and hiding the border moves the content of the box, so we use a pseudo element instead
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    width: '100%',
    border: selected === 'true' ? `2px solid ${theme.palette.primary.main}` : 'none',
    borderRadius: 4,
    pointerEvents: 'none',
    zIndex: 2,
  },

  // Pseudo element for box shadow if row has collapsable content
  ...(hasCollapsable === 'true'
    ? {
        marginBottom: `${theme.spacing(2)} !important`,
        // Pseudo element for box shadow if row has collapsable content
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 'auto 8px -6px 8px',
          height: '8px',
          background: '#ffffff5e',
          borderRadius: '0 0 6px 6px',
          boxShadow: theme.custom.shadows[4],
          zIndex: -1,
        },
      }
    : {}),

  '&:hover': {
    backgroundColor: theme.custom.colors.backgroundLight,
    '&::after': {
      backgroundColor: theme.custom.colors.backgroundLight,
    },
  },
}));

const SubTableContainer = styled(Box)(({ theme }) => ({
  width: '100%',
}));

const InnerTable = styled('div')(({ theme }) => ({
  zIndex: 1,
  position: 'relative',
  display: 'flex',
  width: 'calc(100% - 32px)',
  borderRadius: '6px',
  background: theme.custom.themeColors.white,
  boxShadow: theme.custom.shadows[4],
  alignItems: 'center',
  padding: theme.spacing(2),
  marginTop: theme.spacing(-1),
  marginLeft: theme.spacing(1),
  marginBottom: theme.spacing(1),
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0,
  animation: 'slideDown 0.2s ease-out',
  '@keyframes slideDown': {
    from: {
      opacity: 0,
      transform: 'translateY(-8px)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

/**
 * PortalChild component for rendering collapsable content via portal.
 * Defined outside TableRow to prevent recreation on every render.
 */
const CollapsablePortal: FC<
  PropsWithChildren<{
    onExpand?: () => void;
    containerRef: React.RefObject<HTMLDivElement>;
  }>
> = ({ children, onExpand, containerRef }) => {
  // Call onExpand only once when the portal mounts (row is expanded)
  // Note: Consumers should memoize onExpand with useCallback to avoid unnecessary calls
  useEffect(() => {
    onExpand?.();
  }, [onExpand]);

  // Safety check: don't render portal if container is not available
  if (!containerRef.current) {
    return null;
  }

  return createPortal(
    <InnerTable onClick={stopPropagation}>{children}</InnerTable>,
    containerRef.current
  );
};

const TableRow = <TColumKey extends string | TBaseTableTypes>({
  'data-testid': dataTestId,
  columns,
  selected,
  hasCollapsable,
  onClick,
  onCollapsableExpand,
  children,
}: TTableRowProps<TColumKey> & {
  columns: (TRequiredColumnProps & TTableColumn<TColumKey>)[];
}): JSX.Element => {
  const subTableRef = useRef<HTMLDivElement>(null);

  const [subTableRowExpanded, setSubTableRowExpanded] = useState(false);
  const renderCollapsable =
    columns.findIndex(({ columnKey }) => columnKey === 'collapsable') !== -1 && hasCollapsable;

  const handleCollapsableClick = useCallback(
    (expanded: boolean) => {
      setSubTableRowExpanded(expanded);
      onCollapsableExpand?.(expanded);
    },
    [onCollapsableExpand]
  );

  // Create a stable reference to the Collapsable component to avoid recreation on every render
  const CollapsableComponent = useCallback(
    ({ children, onExpand }: PropsWithChildren<{ onExpand?: () => void }>) => (
      <CollapsablePortal onExpand={onExpand} containerRef={subTableRef}>
        {children}
      </CollapsablePortal>
    ),
    []
  );

  return (
    <>
      <StyledTableRow
        data-testid={dataTestId}
        selected={Booleanish(selected)}
        clickable={Booleanish(!!onClick)}
        has-collapsable={Booleanish(renderCollapsable && !subTableRowExpanded)}
        onClick={onClick}
      >
        {children({
          ...columns
            .filter(({ columnKey }) => columnKey !== 'collapsable')
            .reduce(
              (acc, { columnKey, ...columnProps }) => ({
                ...acc,
                [columnKey.charAt(0).toUpperCase() + columnKey.slice(1)]: ({
                  children,
                }: PropsWithChildren) => (
                  <TableCell
                    expanded={subTableRowExpanded}
                    hasCollapsable={renderCollapsable}
                    columnKey={columnKey}
                    {...columnProps}
                    onCollapsableClick={handleCollapsableClick}
                  >
                    {children}
                  </TableCell>
                ),
              }),
              {} as { [key in Capitalize<TColumKey>]: FC<PropsWithChildren> }
            ),
          // Use a portal to render the collapsable content in the table content after the row
          ...(renderCollapsable && subTableRef.current && subTableRowExpanded
            ? {
                Collapsable: CollapsableComponent,
              }
            : { Collapsable: () => <></> }),
        })}
      </StyledTableRow>
      {/* Target container for the sub table content */}
      <SubTableContainer ref={subTableRef} />
    </>
  );
};

export default TableRow;
